import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MailCheck, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

export default function Contact() {
    const { t } = useTranslation();
    useMeta({
        title: "Contacto · RecomendaLibros",
        description: "Escríbenos. Sugerencias literarias, colaboraciones con librerías independientes, prensa y soporte. Respondemos en 24-48h.",
        canonical: "/contacto",
    });
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/contact", {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                subject: form.subject.trim(),
                message: form.message.trim(),
            });
            setSent(true);
            toast.success(t("contact.sent_toast"));
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <main
            data-testid="contact-page"
            className="min-h-[calc(100vh-73px)] max-w-2xl mx-auto px-5 md:px-10 py-12 md:py-20"
        >
            <Link
                to="/"
                data-testid="contact-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--ink-muted)] hover:text-[var(--accent)] mb-8"
            >
                <ArrowLeft size={14} strokeWidth={1.5} />
                {t("legal.back")}
            </Link>

            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-[var(--border-strong)] mb-6">
                <Mail size={18} strokeWidth={1.2} className="text-[var(--accent)]" />
            </div>

            <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                {t("contact.eyebrow")}
            </p>
            <h1
                className="font-display text-4xl md:text-5xl text-[var(--ink)] tracking-tight mb-5"
                style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}
            >
                {t("contact.title")}
            </h1>
            <p className="text-[var(--ink-muted)] text-lg leading-relaxed mb-10 max-w-xl">
                {t("contact.lead")}
            </p>

            {sent ? (
                <div
                    data-testid="contact-thanks"
                    className="border border-[var(--accent)] bg-[var(--bg-elevated)] p-8 rounded-md max-w-lg"
                >
                    <MailCheck size={28} strokeWidth={1.5} className="text-[var(--accent)] mb-3" />
                    <h2
                        className="font-display text-2xl text-[var(--ink)] mb-3"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("contact.thanks_title")}
                    </h2>
                    <p className="text-[var(--ink-muted)] text-sm leading-relaxed">
                        {t("contact.thanks_lead")}
                    </p>
                </div>
            ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6 max-w-xl">
                    <Field label={t("contact.field_name")}>
                        <input
                            data-testid="contact-name"
                            required minLength={2} maxLength={80}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field label={t("contact.field_email")}>
                        <input
                            data-testid="contact-email"
                            type="email" required maxLength={200}
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field label={t("contact.field_subject")}>
                        <input
                            data-testid="contact-subject"
                            required minLength={2} maxLength={160}
                            value={form.subject}
                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                            className={inputCls}
                        />
                    </Field>
                    <Field label={t("contact.field_message")}>
                        <textarea
                            data-testid="contact-message"
                            required minLength={5} maxLength={4000}
                            rows={6}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className={`${inputCls} resize-none`}
                        />
                    </Field>
                    <button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={busy}
                        className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        {t("contact.submit")}
                    </button>
                </form>
            )}
        </main>
    );
}

const inputCls =
    "w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                {label}
            </span>
            {children}
        </label>
    );
}
